/**
 * What an invoice was for, and where it stands — as codes the apps translate.
 *
 * The invoice list used to carry an English `label` the Stripe webhook wrote
 * into the row ("Journey Unlock", "Pro Plan (Yearly)"), so the pt-PT billing page
 * read "Journey Unlock" (JW-51), and the status badge printed the raw enum for
 * anything but `paid`. The server now derives `kind` from the row's recorded
 * operation and plan code (`invoiceKindOf` in platform-api), and each app draws
 * both through these label keys.
 */

export const INVOICE_KINDS = [
  'journey_unlock',
  'journey_renewal',
  'talent_sponsorship',
  'pro_subscription',
  'enterprise_subscription',
  'family_addon',
  /** A subscription invoice whose plan the row does not record. */
  'subscription',
] as const;

export type InvoiceKind = (typeof INVOICE_KINDS)[number];

/** Mirrors `billing_invoice_status` (Stripe's invoice lifecycle). */
export const INVOICE_STATUSES = ['draft', 'open', 'paid', 'void', 'uncollectible'] as const;

export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const INVOICE_KIND_LABEL_KEY = {
  journey_unlock: 'billing.invoiceKind.journey_unlock',
  journey_renewal: 'billing.invoiceKind.journey_renewal',
  talent_sponsorship: 'billing.invoiceKind.talent_sponsorship',
  pro_subscription: 'billing.invoiceKind.pro_subscription',
  enterprise_subscription: 'billing.invoiceKind.enterprise_subscription',
  family_addon: 'billing.invoiceKind.family_addon',
  subscription: 'billing.invoiceKind.subscription',
} as const satisfies Record<InvoiceKind, string>;

export const INVOICE_STATUS_LABEL_KEY = {
  draft: 'billing.invoiceStatus.draft',
  open: 'billing.invoiceStatus.open',
  paid: 'billing.invoiceStatus.paid',
  void: 'billing.invoiceStatus.void',
  uncollectible: 'billing.invoiceStatus.uncollectible',
} as const satisfies Record<InvoiceStatus, string>;
